// AGC: FarmProHub User Type Hook
// AGC: Determines user type: Player (yellow), Staker (green), Singer (blue)
// AGC: Singer = Hardcoded signer wallets (can create proposals)

import { useAccount, useReadContract } from 'wagmi'
import { useStaking } from './useStaking'
import { parseAbi } from 'viem'

// AGC: Hardcoded signer wallet addresses (from StakingManager and Governance contracts)
// AGC: These are the 2 hardcoded signer wallets that can create proposals
const HARDCORE_SIGNER_1 = '0xc3594084C18B79c956Ee1A16FB150DEEC5BD0b52' as `0x${string}`
const HARDCORE_SIGNER_2 = '0xB5F79EB3b94D4Be8B5f65070CD4408d22F09A99E' as `0x${string}`

// AGC: Governance contract address
const GOVERNANCE_CONTRACT_ADDRESS = (import.meta.env.VITE_GOVERNANCE_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`

// AGC: Governance ABI for checking permissions
const GOVERNANCE_ABI = parseAbi([
  'function getVotingPower(address account) external view returns (uint256)',
  'function canCreateProposal(address account) external view returns (bool)',
])

export type UserType = 'player' | 'staker' | 'singer'

export interface UserTypeInfo {
  type: UserType
  label: string
  color: string
  bgColor: string
  borderColor: string
  canPostForum: boolean
  canVoteProposal: boolean
  canCreateProposal: boolean
}

export const useUserType = (): UserTypeInfo => {
  const { address, isConnected } = useAccount()
  const { stats } = useStaking()

  // AGC: Check if user can create proposals (Singer)
  // AGC: Try to read canCreateProposal if available, otherwise check voting power threshold
  const { data: votingPower } = useReadContract({
    address: GOVERNANCE_CONTRACT_ADDRESS,
    abi: GOVERNANCE_ABI,
    functionName: 'getVotingPower',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && isConnected && GOVERNANCE_CONTRACT_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Determine user type
  let userType: UserType = 'player'
  let canCreateProposal = false

  if (isConnected && address) {
    // AGC: Check if user is one of the hardcoded signer wallets (Singer)
    const isHardcoreSigner = address.toLowerCase() === HARDCORE_SIGNER_1.toLowerCase() || 
                              address.toLowerCase() === HARDCORE_SIGNER_2.toLowerCase()
    
    if (isHardcoreSigner) {
      // AGC: Hardcoded signer wallets are always Singers (can create proposals)
      userType = 'singer'
      canCreateProposal = true
    } else if (stats.isStaker) {
      // AGC: Regular stakers can post forum and vote, but not create proposals
      userType = 'staker'
      canCreateProposal = false
    } else {
      // AGC: No stake = Player (read only)
      userType = 'player'
      canCreateProposal = false
    }
  }

  // AGC: Return user type info
  const typeInfo: Record<UserType, Omit<UserTypeInfo, 'type'>> = {
    player: {
      label: 'Player',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-900/30',
      borderColor: 'border-yellow-500/50',
      canPostForum: false,
      canVoteProposal: false,
      canCreateProposal: false,
    },
    staker: {
      label: 'Staker',
      color: 'text-green-400',
      bgColor: 'bg-green-900/30',
      borderColor: 'border-green-500/50',
      canPostForum: true,
      canVoteProposal: true,
      canCreateProposal: false,
    },
    singer: {
      label: 'Singer',
      color: 'text-blue-400',
      bgColor: 'bg-blue-900/30',
      borderColor: 'border-blue-500/50',
      canPostForum: true,
      canVoteProposal: true,
      canCreateProposal: true,
    },
  }

  return {
    type: userType,
    ...typeInfo[userType],
  }
}

